import React, { useEffect, useRef } from 'react';
import { Star, ExternalLink, Users, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';

const TrustpilotSection = () => {
  const trustboxRef = useRef(null);
  const carouselRef = useRef(null);
  
  // Trustpilot configuration - LIVE
  const TRUSTPILOT_BUSINESS_UNIT_ID = '696be5d962f8fe8a8cb1b882';
  const TRUSTPILOT_REVIEW_URL = 'https://www.trustpilot.com/review/propindexa.online';

  useEffect(() => {
    // Load Trustpilot widget script
    const script = document.createElement('script');
    script.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
    script.async = true;
    script.onload = () => {
      // Initialize Trustpilot widgets after script loads
      if (window.Trustpilot) {
        if (trustboxRef.current) {
          window.Trustpilot.loadFromElement(trustboxRef.current, true);
        }
        if (carouselRef.current) {
          window.Trustpilot.loadFromElement(carouselRef.current, true);
        }
      }
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src*="trustpilot"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <section style={{
      padding: '120px 24px',
      background: 'var(--bg-primary)',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Section Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '48px'
        }}>
          {/* Trustpilot Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(0, 182, 122, 0.1)',
            padding: '8px 16px',
            borderRadius: '20px',
            marginBottom: '24px'
          }}>
            <Star size={16} fill="#00b67a" color="#00b67a" />
            <span style={{ color: '#00b67a', fontSize: '14px', fontWeight: 600 }}>
              Rated Excellent on Trustpilot
            </span>
          </div>

          <h2 style={{
            fontSize: '48px',
            fontWeight: 700,
            marginBottom: '16px',
            color: 'var(--text-primary)'
          }} className="display-md">
            Trusted by Traders Worldwide
          </h2>
          <p style={{
            fontSize: '18px',
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto 40px'
          }} className="body-lg">
            See what our funded traders have to say about their experience with Prop Indexa
          </p>

          {/* Trustpilot Mini Widget - Shows overall rating */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '40px'
          }}>
            <div
              ref={trustboxRef}
              className="trustpilot-widget"
              data-locale="en-US"
              data-template-id="5419b6a8b0d04a076446a9ad"
              data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT_ID}
              data-style-height="24px"
              data-style-width="100%"
              data-theme="dark"
              data-min-review-count="0"
              data-style-alignment="center"
            >
              <a href={TRUSTPILOT_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                Trustpilot
              </a>
            </div>
          </div>
        </div>

        {/* Trustpilot Review Carousel - Shows actual reviews */}
        <div style={{
          marginBottom: '48px',
          background: 'var(--bg-secondary)',
          borderRadius: '16px',
          padding: '32px',
          border: '1px solid var(--border-subtle)'
        }}>
          <div
            ref={carouselRef}
            className="trustpilot-widget"
            data-locale="en-US"
            data-template-id="54ad5defc6454f065c28af8b"
            data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT_ID}
            data-style-height="240px"
            data-style-width="100%"
            data-theme="dark"
            data-stars="4,5"
            data-review-languages="en"
            data-font-family="Montserrat"
          >
            <a href={TRUSTPILOT_REVIEW_URL} target="_blank" rel="noopener noreferrer">
              Trustpilot Reviews
            </a>
          </div>
        </div>

        {/* Stats Row */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '48px',
          flexWrap: 'wrap',
          marginBottom: '48px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: 'rgba(0, 182, 122, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px'
            }}>
              <Star size={28} fill="#00b67a" color="#00b67a" />
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>Trust Score</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#00b67a' }}>Excellent</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: 'rgba(0, 182, 122, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px'
            }}>
              <Users size={28} color="#00b67a" />
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>Happy Traders</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)' }}>500+</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: 'rgba(0, 182, 122, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px'
            }}>
              <MessageSquare size={28} color="#00b67a" />
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>Verified Reviews</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)' }}>Trustpilot</div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <Button
            onClick={() => window.open(`https://www.trustpilot.com/evaluate/${TRUSTPILOT_BUSINESS_UNIT_ID}`, '_blank')}
            className="btn-primary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '16px 32px',
              fontSize: '16px'
            }}
          >
            <Star size={18} />
            Write a Review
            <ExternalLink size={16} />
          </Button>
          <Button
            onClick={() => window.open(TRUSTPILOT_REVIEW_URL, '_blank')}
            variant="outline"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '16px 32px',
              fontSize: '16px',
              background: 'transparent',
              border: '1px solid var(--border-primary)'
            }}
          >
            View All Reviews on Trustpilot
            <ExternalLink size={16} />
          </Button>
        </div>

        {/* Trustpilot Logo Attribution */}
        <div style={{
          textAlign: 'center',
          marginTop: '32px'
        }}>
          <a 
            href={TRUSTPILOT_REVIEW_URL} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--text-muted)',
              textDecoration: 'none',
              fontSize: '13px'
            }}
          >
            <svg width="90" height="22" viewBox="0 0 126 31" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M33.3086 11.6465H39.3164V13.0742H35.0137V16.5234H38.9453V17.9512H35.0137V23H33.3086V11.6465ZM43.6289 15.1855C44.2383 15.1855 44.7695 15.2832 45.2227 15.4785C45.6758 15.6738 46.0508 15.957 46.3477 16.3281C46.6445 16.6992 46.8672 17.1504 47.0156 17.6816C47.1641 18.2129 47.2383 18.8145 47.2383 19.4863V20.0977H41.6406C41.6719 20.9883 41.8984 21.6719 42.3203 22.1484C42.7422 22.625 43.3398 22.8633 44.1133 22.8633C44.6758 22.8633 45.1484 22.7598 45.5312 22.5527C45.9141 22.3457 46.2266 22.0625 46.4688 21.7031L47.1211 22.6875C46.8086 23.1094 46.3945 23.4531 45.8789 23.7188C45.3633 23.9844 44.7227 24.1172 43.957 24.1172C43.3633 24.1172 42.8242 24.0195 42.3398 23.8242C41.8555 23.6289 41.4375 23.3418 41.0859 22.9629C40.7344 22.584 40.4609 22.1133 40.2656 21.5508C40.0703 20.9883 39.9727 20.3398 39.9727 19.6055C39.9727 18.8867 40.0664 18.2422 40.2539 17.6719C40.4414 17.1016 40.707 16.6211 41.0508 16.2305C41.3945 15.8398 41.8047 15.5449 42.2812 15.3457C42.7578 15.1465 43.2773 15.0488 43.8398 15.0488L43.6289 15.1855ZM43.6992 16.2715C43.0273 16.2715 42.4922 16.4824 42.0938 16.9043C41.6953 17.3262 41.4609 17.9199 41.3906 18.6855H45.7969C45.7812 17.9512 45.5977 17.3652 45.2461 16.9277C44.8945 16.4902 44.3828 16.2715 43.6992 16.2715Z" fill="#00b67a"/>
              <path d="M54.7891 15.1855L52.0898 23.8594H50.1855L47.4863 15.1855H49.1914L51.1309 21.8984L53.0703 15.1855H54.7891ZM56.0449 11.6465H57.75V13.0742H56.0449V11.6465ZM56.0449 15.1855H57.75V23.8594H56.0449V15.1855ZM63.2812 15.0488C63.875 15.0488 64.4062 15.1465 64.875 15.3418C65.3438 15.5371 65.7422 15.8242 66.0703 16.2031C66.3984 16.582 66.6484 17.0469 66.8203 17.5977C66.9922 18.1484 67.0781 18.7773 67.0781 19.4844C67.0781 20.207 66.9844 20.8477 66.7969 21.4062C66.6094 21.9648 66.3438 22.4336 66 22.8125C65.6562 23.1914 65.2422 23.4785 64.7578 23.6738C64.2734 23.8691 63.7305 23.9668 63.1289 23.9668C62.543 23.9668 62.0117 23.8691 61.5352 23.6738C61.0586 23.4785 60.6523 23.1914 60.3164 22.8125C59.9805 22.4336 59.7227 21.9648 59.543 21.4062C59.3633 20.8477 59.2734 20.207 59.2734 19.4844C59.2734 18.7773 59.3594 18.1484 59.5312 17.5977C59.7031 17.0469 59.9531 16.582 60.2812 16.2031C60.6094 15.8242 61.0078 15.5371 61.4766 15.3418C61.9453 15.1465 62.4766 15.0488 63.0703 15.0488L63.2812 15.1855ZM63.1289 16.3418C62.4102 16.3418 61.8516 16.5996 61.4531 17.1152C61.0547 17.6309 60.8555 18.4082 60.8555 19.4473C60.8555 20.5176 61.0508 21.3164 61.4414 21.8438C61.832 22.3711 62.3945 22.6348 63.1289 22.6348C63.8633 22.6348 64.4258 22.3711 64.8164 21.8438C65.207 21.3164 65.4023 20.5176 65.4023 19.4473C65.4023 18.4082 65.207 17.6309 64.8164 17.1152C64.4258 16.5996 63.8633 16.3418 63.1289 16.3418Z" fill="#00b67a"/>
              <path d="M72.457 15.0488L72.5977 16.6836C72.9648 16.123 73.4141 15.6934 73.9453 15.3945C74.4766 15.0957 75.0781 14.9473 75.75 14.9473L75.8906 16.6133C75.1406 16.6133 74.5 16.8086 73.9688 17.1992C73.4375 17.5898 73.0391 18.1445 72.7734 18.8633V23.8594H71.0684V15.1855H72.457V15.0488ZM85.0781 15.1855V23.8594H83.6895L83.5488 22.0879C83.1973 22.7129 82.7559 23.1895 82.2246 23.5176C81.6934 23.8457 81.0625 24.0098 80.332 24.0098C79.3555 24.0098 78.5703 23.6934 77.9766 23.0605C77.3828 22.4277 77.0859 21.4434 77.0859 20.1074V15.1855H78.791V19.9219C78.791 20.8281 78.957 21.5 79.2891 21.9375C79.6211 22.375 80.1211 22.5938 80.7891 22.5938C81.457 22.5938 82.0273 22.3555 82.5 21.8789C82.9727 21.4023 83.3164 20.7773 83.5312 20.0039V15.1855H85.0781ZM90.5625 23.9668C89.3594 23.9668 88.4102 23.5762 87.7148 22.7949C87.0195 22.0137 86.6719 20.9238 86.6719 19.5254C86.6719 18.1426 87.0156 17.0527 87.7031 16.2559C88.3906 15.459 89.332 15.0605 90.5273 15.0605C91.0898 15.0605 91.5781 15.1504 91.9922 15.3301C92.4062 15.5098 92.7539 15.7578 93.0352 16.0742L92.3828 17.0586C92.1328 16.8086 91.8633 16.6133 91.5742 16.4727C91.2852 16.332 90.9453 16.2617 90.5547 16.2617C89.7812 16.2617 89.1836 16.5254 88.7617 17.0527C88.3398 17.5801 88.1289 18.3867 88.1289 19.4727C88.1289 20.5586 88.3516 21.3809 88.7969 21.9395C89.2422 22.498 89.8516 22.7773 90.625 22.7773C91.0156 22.7773 91.3711 22.7012 91.6914 22.5488C92.0117 22.3965 92.2891 22.1895 92.5234 21.9277L93.1406 22.8945C92.8438 23.2344 92.4648 23.5059 92.0039 23.709C91.543 23.9121 91.0312 24.0137 90.4688 24.0137L90.5625 23.9668ZM97.5938 15.0488C98.1875 15.0488 98.7188 15.1465 99.1875 15.3418C99.6562 15.5371 100.055 15.8242 100.383 16.2031C100.711 16.582 100.961 17.0469 101.133 17.5977C101.305 18.1484 101.391 18.7773 101.391 19.4844C101.391 20.207 101.297 20.8477 101.109 21.4062C100.922 21.9648 100.656 22.4336 100.312 22.8125C99.9688 23.1914 99.5547 23.4785 99.0703 23.6738C98.5859 23.8691 98.043 23.9668 97.4414 23.9668C96.8555 23.9668 96.3242 23.8691 95.8477 23.6738C95.3711 23.4785 94.9648 23.1914 94.6289 22.8125C94.293 22.4336 94.0352 21.9648 93.8555 21.4062C93.6758 20.8477 93.5859 20.207 93.5859 19.4844C93.5859 18.7773 93.6719 18.1484 93.8438 17.5977C94.0156 17.0469 94.2656 16.582 94.5938 16.2031C94.9219 15.8242 95.3203 15.5371 95.7891 15.3418C96.2578 15.1465 96.7891 15.0488 97.3828 15.0488L97.5938 15.1855ZM97.4414 16.3418C96.7227 16.3418 96.1641 16.5996 95.7656 17.1152C95.3672 17.6309 95.168 18.4082 95.168 19.4473C95.168 20.5176 95.3633 21.3164 95.7539 21.8438C96.1445 22.3711 96.707 22.6348 97.4414 22.6348C98.1758 22.6348 98.7383 22.3711 99.1289 21.8438C99.5195 21.3164 99.7148 20.5176 99.7148 19.4473C99.7148 18.4082 99.5195 17.6309 99.1289 17.1152C98.7383 16.5996 98.1758 16.3418 97.4414 16.3418ZM106.512 15.0488L106.652 16.6836C107.02 16.123 107.469 15.6934 108 15.3945C108.531 15.0957 109.133 14.9473 109.805 14.9473L109.945 16.6133C109.195 16.6133 108.555 16.8086 108.023 17.1992C107.492 17.5898 107.094 18.1445 106.828 18.8633V23.8594H105.123V15.1855H106.512V15.0488ZM111.785 11.6465H113.49V13.0742H111.785V11.6465ZM111.785 15.1855H113.49V23.8594H111.785V15.1855ZM119.488 15.0488C119.957 15.0488 120.359 15.1387 120.695 15.3184C121.031 15.498 121.301 15.7656 121.504 16.1211L120.887 17.0352C120.637 16.5508 120.234 16.3086 119.68 16.3086C119.285 16.3086 118.961 16.4102 118.707 16.6133C118.453 16.8164 118.281 17.0977 118.191 17.457C118.102 17.8164 118.057 18.2383 118.057 18.7227V23.8594H116.352V15.1855H117.75L117.879 16.7852C118.113 16.1758 118.449 15.7148 118.887 15.4023C119.324 15.0898 119.848 14.9336 120.457 14.9336L119.488 15.0488Z" fill="#00b67a"/>
              <path d="M15.5 0L19.0675 11.0184H30.6403L21.2864 17.8232L24.8539 28.8416L15.5 22.0368L6.14608 28.8416L9.71359 17.8232L0.359672 11.0184H11.9325L15.5 0Z" fill="#00b67a"/>
            </svg>
            <span>Reviews powered by Trustpilot</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default TrustpilotSection;
